import { useState } from 'react';
import DataGridLayout from '../../components/layout/DataGridLayout';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Mail, Phone, Trash2, User, Tag, BookOpen } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import VerificationToggle from '../../components/ui/VerificationToggle';
import { EnquiryRequest } from '../../types/enquiry';

const MOCK_OFFER_REGISTRATIONS: EnquiryRequest[] = [
  {
    id: '1',
    emailId: 'peyyalaharipriya321@gmail.com',
    mobileNo: '7989163318',
    fullName: 'Peyyala Hari Priya',
    couponCode: 'ANNIVERSARY20',
    course: 'Python Full Stack',
    isVerified: true
  },
  {
    id: '2',
    emailId: 'alekhyaguddeti04@gmail.com',
    mobileNo: '+916300378755',
    fullName: 'Alekhya Guddeti',
    couponCode: 'ANNIVERSARY20',
    course: 'Python AI and ML',
    isVerified: false
  },
  {
    id: '3',
    emailId: 'sagarreddyguvvala@gmail.com',
    mobileNo: '+918919575710',
    fullName: 'Sagar Reddy',
    couponCode: 'ANNIVERSARY20',
    course: 'Data Science',
    isVerified: true
  },
  {
    id: '4',
    emailId: 'sagarreddyguvvala.77@gmail.com',
    mobileNo: '+919494744282',
    fullName: 'Sagar Guvvala',
    couponCode: 'ANNIVERSARY20',
    course: 'Python AI and ML',
    isVerified: true
  },
  {
    id: '5',
    emailId: 'sasih2415@gmail.com',
    mobileNo: '9392047497',
    fullName: 'Sasikanth',
    couponCode: 'ANNIVERSARY20',
    course: 'Web Angular / React JS',
    isVerified: true
  },
  {
    id: '6',
    emailId: 'vathsav49@gmail.com',
    mobileNo: '8187012974',
    fullName: 'Vathsav',
    couponCode: 'ANNIVERSARY20',
    course: 'Web Angular / React JS',
    isVerified: false
  },
  {
    id: '7',
    emailId: 'mogallapalligowtham@gmail.com',
    mobileNo: '8309400121',
    fullName: 'Gowtham Mogallapalli',
    couponCode: 'ANNIVERSARY20',
    course: 'Python Full Stack',
    isVerified: true
  }
];

const OfferRegistrations = () => {
  const [registrations, setRegistrations] = useState<EnquiryRequest[]>(MOCK_OFFER_REGISTRATIONS);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleRefresh = () => {
    toast({
      title: "Refreshed",
      description: "Offer registration data has been refreshed successfully.",
    });
  };

  const handleExport = () => {
    toast({
      title: "Export Started",
      description: "Your offer registration data export has been initiated.",
    });
  };

  const handleToggleVerification = (id: string) => {
    setRegistrations(prevRegistrations => 
      prevRegistrations.map(registration => 
        registration.id === id 
          ? { ...registration, isVerified: !registration.isVerified } 
          : registration
      )
    );

    toast({
      title: "Verification Status Updated",
      description: "The verification status has been updated successfully.",
    });
  };

  const handleDelete = (id: string) => {
    setRegistrations(prevRegistrations => prevRegistrations.filter(registration => registration.id !== id));
    
    toast({
      title: "Entry Deleted",
      description: "The offer registration has been removed successfully.",
    });
  };

  const filteredRegistrations = registrations.filter(registration => 
    registration.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    registration.emailId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    registration.mobileNo.includes(searchQuery) ||
    (registration.couponCode && registration.couponCode.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (registration.course && registration.course.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <DataGridLayout
      title="Offer Registrations"
      subtitle="Manage offer and coupon code registrations"
      searchPlaceholder="Search by name, email, course or coupon code..."
      onSearch={handleSearch}
      onRefresh={handleRefresh}
      onExport={handleExport}
    >
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px]">Email ID</TableHead>
              <TableHead>Mobile No</TableHead>
              <TableHead>Coupon Code</TableHead>
              <TableHead>Course</TableHead>
              <TableHead className="text-center">Is Verified</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRegistrations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No offer registrations found
                </TableCell>
              </TableRow>
            ) : (
              filteredRegistrations.map((registration) => (
                <TableRow key={registration.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-blue-500" />
                      {registration.emailId}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-green-500" />
                      {registration.mobileNo}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Tag size={16} className="text-orange-500" />
                      {registration.couponCode}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} className="text-purple-500" />
                      {registration.course}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <VerificationToggle 
                      isVerified={registration.isVerified} 
                      onToggle={() => handleToggleVerification(registration.id)}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(registration.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </DataGridLayout>
  );
};

export default OfferRegistrations;